import { v4 as uuidv4 } from 'uuid';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import GS from '../../grafsync/grafsync';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import PropTypes from 'prop-types';
import React from 'react';
import CancelButton from '../../core/CancelButton';
import PrimaryButton from '../../core/PrimaryButton';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import ConceptChip from '../core/ConceptChip';
import FindConcept from './FindConcept';
import { makeCancelable } from '../../core/utils/utils';
import Errored from '../../core/Errored';

const LANG_PROPS = [
    "example",
    "definition",
    "changeNote",
    "historyNote",
    "editorialNote",
    "scopeNote"
];

const MERGE_PROPS = [
    {
        label: "Preferred Label",
        property: "prefLabel"
    },
    {
        label: "Alternative Label",
        property: "altLabel"
    },
    {
        label: "Hidden Label",
        property: "hiddenLabel"
    },
    {
        label: "Narrower",
        property: "narrower"
    },
    {
        label: "Related",
        property: "related"
    },
    {
        label: "Broad Match",
        property: "broadMatch"
    },
    {
        label: "Close Match",
        property: "closeMatch"
    },
    {
        label: "Exact Match",
        property: "exactMatch"
    },
    {
        label: "Narrow Match",
        property: "narrowMatch"
    },
    {
        label: "Related Match",
        property: "relatedMatch"
    }, {
        label: "Change Note",
        property: "changeNote"
    },
    {
        label: "Definition",
        property: "definition"
    },
    {
        label: "Editorial Note",
        property: "editorialNote"
    },
    {
        label: "Example",
        property: "example"
    },
    {
        label: "History Note",
        property: "historyNote"
    },
    {
        label: "Scope Note",
        property: "scopeNote"
    }
];

class MergeDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            disable: false,
            target: undefined,
            merging: false,
            isErrored: undefined,
            mergeList: MERGE_PROPS.map(mp => mp.property)
        };
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    handleClickOpen = () => this.setState({ open: true });

    setTarget = (target) => this.setState({ target });

    removeTarget = () => this.setState({ target: null });

    loadData(data, options) {
        this.cancellable = makeCancelable(GS.loadData(data, options, this.props.graph, false));
        return this.cancellable;
    }

    async retrieveXlLabels(data) {
        let xlPL = data.xlPrefLabel ? [...data.xlPrefLabel] : [];
        let xlAL = data.xlAltLabel ? [...data.xlAltLabel] : [];
        let xlHL = data.xlHiddenLabel ? [...data.xlHiddenLabel] : [];
        // Make sure we dedupe in case of misuse
        const labelResources = [...new Set([...xlPL, ...xlAL, ...xlHL])];
        if (labelResources.length > 0) {
            const labelLoadOptions = {
                predicates: [
                    "http://www.w3.org/2008/05/skos-xl#literalForm",
                    "http://www.w3.org/2004/02/skos/core#inScheme",
                    "https://grafsync.graphifi.com/schema/gs/status"
                ]
            };
            GS.handle("retrieve-check");
            await this.loadData(labelResources, labelLoadOptions, false).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            // Now that we have loaded any XL labels we force entailment to compute alt and hidden labels
            // Pref labels would already have been computed potentially
            GS.computeEntailment([{
                iri: data.id,
                graph: this.props.graph
            }]);
        }
    }

    getLabelCounter() {
        const { schemeIRI, graph, counters } = this.props;
        this.cancellable = makeCancelable(GS.getCounter(schemeIRI, graph, "secondary", counters.secondaryCounter));
        return this.cancellable;
    }

    addNewLabel = async (property, language, label) => {
        let exists = true;
        let existsFromAPI = false;
        let labelIRI;
        let reservationID;
        while (exists) {
            // If exists and label based we need to add a random number to end
            // This matches the logic for RDF merge
            labelIRI = await this.getIRI(label, language);
            if (existsFromAPI && !this.props.useUuidIRI && !this.props.useCounterIRI) {
                labelIRI += "-" + uuidv4();
            }
            GS.handle("label-check");
            const isFound = await GS.reserveIRI(labelIRI, this.props.graph, "http://www.w3.org/2008/05/skos-xl#Label");
            exists = isFound.exists;
            existsFromAPI = exists;
            if (!exists) reservationID = isFound.id;
        }
        const newXLlabel = {
            id: labelIRI,
            type: "Label",
            inScheme: [this.props.schemeIRI],
            literalForm: { [language]: label }
        };
        const additionalChannels = [this.props.conceptIRI];
        await GS.insertResource(newXLlabel, labelIRI, this.props.graph, additionalChannels, reservationID);
        GS.addProperty(this.props.conceptIRI, this.props.graph, property, labelIRI);
    };

    addNewComment = async (property, language, comment) => {
        // Very similar code to ConceptDetail
        let exists = true;
        let noteIRI;
        let reservationID;
        while (exists) {
            const slashHash = this.props.useSlashIRI ? (this.props.schemeIRI.endsWith('/') ? '' : '/') : '#';
            const iriID = uuidv4();
            // Cater for hash in base IRI
            // Notes as resources are always UUID based for now
            const baseIRI = this.props.schemeIRI.indexOf('#') > 0 ?
                this.props.schemeIRI.substring(0, this.props.schemeIRI.indexOf('#'))
                :
                this.props.schemeIRI;
            noteIRI = baseIRI + slashHash + iriID;
            GS.handle("comment-check");
            const isFound = await GS.reserveIRI(noteIRI, this.props.graph, "https://graphifi.com/schema/xkos/Note");
            exists = isFound.exists;
            if (!exists) reservationID = isFound.id;
        }
        const newNote = {
            id: noteIRI,
            type: "Note",
            inScheme: [this.props.schemeIRI],
            comment: { [language]: comment }
        };
        const additionalChannels = [this.props.conceptIRI];
        await GS.insertResource(newNote, noteIRI, this.props.graph, additionalChannels, reservationID);
        // We're overriding the alias
        GS.addPropertyAsIRI(this.props.conceptIRI, this.props.graph, property, noteIRI);
    };

    getIRI = async (text, labelLang) => {
        let labelComputedIRI = "";
        if (this.props.useCounterIRI) {
            const labelCountData = await this.getLabelCounter().promise;
            const labelCount = labelCountData.count;
            labelComputedIRI = GS.computeCounterIRI(this.props.schemeIRI, this.props.useSlashIRI, labelCount);
        }
        else {
            const iriOptions = {
                prependText: this.props.iriOptions.xlOptions.prependText,
                appendText: this.props.iriOptions.xlOptions.shouldAppendText ? labelLang : "",
                textSeparator: "-"
            };
            labelComputedIRI = GS.computeIRI(this.props.schemeIRI, text, this.props.useSlashIRI, this.props.useUuidIRI, iriOptions);
        }
        return labelComputedIRI;
    };

    mergeConcept = async () => {
        this.setState({ merging: true }, async () => {
            try {
                const { conceptIRI, graph } = this.props;
                const mergeList = this.state.mergeList;
                // At this point we can assume both the source and target concepts are loaded
                let sourceData = GS.data(conceptIRI, graph);
                let targetData = GS.data(this.state.target, graph);
                // We deprecate first to stop further updates to the target being merged
                if (!targetData["deprecated"]) {
                    GS.addProperty(this.state.target, graph, "deprecated", "true", null, "http://www.w3.org/2001/XMLSchema#boolean");
                }
                // First we need to load any XL labels on source and target
                // If anybody adds to this in the meantime that's alright as that will force entailment computation so we'll have the values
                // When we assign source and target data again
                GS.handle("merge-check");
                await this.retrieveXlLabels(sourceData);
                await this.retrieveXlLabels(targetData);
                const sourceDataXL = GS.data(conceptIRI, graph);
                const targetDataXL = GS.data(this.state.target, graph);
                let isBeingMerged = [];
                if (mergeList.includes("prefLabel")) isBeingMerged.push("xlPrefLabel");
                if (mergeList.includes("altLabel")) isBeingMerged.push("xlAltLabel");
                if (mergeList.includes("hiddenLabel")) isBeingMerged.push("xlHiddenLabel");
                for (let labelProp of ["xlPrefLabel", "xlAltLabel", "xlHiddenLabel"]) {
                    if (isBeingMerged.includes(labelProp)) {
                        const xlLabels = targetDataXL[labelProp];
                        if (xlLabels) {
                            for (let labelIRI of xlLabels) {
                                const labelData = GS.data(labelIRI, graph);
                                const labelLang = Object.keys(labelData.literalForm)[0];
                                const literalForm = labelData.literalForm[labelLang];
                                let existingValues = [];
                                ["prefLabel", "altLabel", "hiddenLabel"].forEach(sl => {
                                    const slExistsOnSource = sourceDataXL[sl] && sourceDataXL[sl][labelLang];
                                    if (slExistsOnSource) {
                                        const slArrrayValues = Array.isArray(sourceDataXL[sl][labelLang]) ? sourceDataXL[sl][labelLang] : [sourceDataXL[sl][labelLang]];
                                        existingValues = existingValues.concat(slArrrayValues);
                                    }
                                });
                                const exists = existingValues.includes(literalForm);
                                if (!exists) {
                                    // Preferred labels become alt labels
                                    const mergeLabelProp = labelProp === "xlPrefLabel" ? "xlAltLabel" : labelProp;
                                    await this.addNewLabel(mergeLabelProp, labelLang, literalForm);
                                }
                            }
                        }
                    }
                }
                sourceData = GS.data(conceptIRI, graph);
                targetData = GS.data(this.state.target, graph);
                ["prefLabel", "altLabel", "hiddenLabel"].forEach(labelProp => {
                    if (mergeList.includes(labelProp)) {
                        const values = targetData[labelProp];
                        if (values) {
                            Object.keys(values).forEach(lang => {
                                const langValues = values[lang];
                                if (langValues) {
                                    const arrayValues = Array.isArray(langValues) ? langValues : [langValues];
                                    arrayValues.forEach(av => {
                                        let existingValues = [];
                                        ["prefLabel", "altLabel", "hiddenLabel"].forEach(sl => {
                                            const slExistsOnSource = sourceData[sl] && sourceData[sl][lang];
                                            if (slExistsOnSource) {
                                                const slArrrayValues = Array.isArray(sourceData[sl][lang]) ? sourceData[sl][lang] : [sourceData[sl][lang]];
                                                existingValues = existingValues.concat(slArrrayValues);
                                            }
                                        });
                                        const exists = existingValues.includes(av);
                                        if (!exists) {
                                            // Preferred labels become alt labels
                                            const mappedLabelProp = labelProp === "prefLabel" ? "altLabel" : labelProp;
                                            GS.addProperty(conceptIRI, graph, mappedLabelProp, av, lang);
                                        }
                                    });
                                }
                            });
                        }
                    }
                });
                ["narrower", "related"].forEach(relProp => {
                    const values = targetData[relProp];
                    if (mergeList.includes(relProp)) {
                        if (values) {
                            values.forEach(value => {
                                const arrayValues = Array.isArray(value) ? value : [value];
                                arrayValues.forEach(av => {
                                    let existingValues = [];
                                    ["narrower", "related", "broader"].forEach(sl => {
                                        const slExistsOnSource = sourceData[sl];
                                        if (slExistsOnSource) {
                                            const slArrrayValues = Array.isArray(sourceData[sl]) ? sourceData[sl] : [sourceData[sl]];
                                            existingValues = existingValues.concat(slArrrayValues);
                                        }
                                    });
                                    const exists = existingValues.includes(av);
                                    if (!exists) {
                                        GS.addProperty(conceptIRI, graph, relProp, av);
                                    }
                                });
                            });
                        }
                    }
                });
                ["relatedMatch", "exactMatch", "broadMatch", "closeMatch", "narrowMatch"].forEach(relProp => {
                    if (mergeList.includes(relProp)) {
                        const values = targetData[relProp];
                        if (values) {
                            values.forEach(value => {
                                const arrayValues = Array.isArray(value) ? value : [value];
                                arrayValues.forEach(av => {
                                    let existingValues = [];
                                    ["relatedMatch", "exactMatch", "broadMatch", "closeMatch", "narrowMatch"].forEach(sm => {
                                        const smExistsOnSource = sourceData[sm];
                                        if (smExistsOnSource) {
                                            const slArrrayValues = Array.isArray(sourceData[sm]) ? sourceData[sm] : [sourceData[sm]];
                                            existingValues = existingValues.concat(slArrrayValues);
                                        }
                                    });
                                    const exists = existingValues.includes(av);
                                    if (!exists) {
                                        GS.addProperty(conceptIRI, graph, relProp, av);
                                    }
                                });
                            });
                        }
                    }
                });
                // Language tagged props. Some of these might be XL or resources
                LANG_PROPS.forEach(labelProp => {
                    if (mergeList.includes(labelProp)) {
                        const values = targetData[labelProp];
                        if (values) {
                            Object.keys(values).forEach(lang => {
                                const langValues = values[lang];
                                if (langValues) {
                                    const arrayValues = Array.isArray(langValues) ? langValues : [langValues];
                                    arrayValues.forEach(av => {
                                        const langPropExistsOnSource = sourceData[labelProp] && sourceData[labelProp][lang];
                                        if (langPropExistsOnSource) {
                                            const existingArrrayValues = Array.isArray(sourceData[labelProp][lang]) ? sourceData[labelProp][lang] : [sourceData[labelProp][lang]];
                                            const exists = existingArrrayValues.includes(av);
                                            if (!exists) {
                                                GS.addProperty(conceptIRI, graph, labelProp, av, lang);
                                            }
                                        }
                                        else {
                                            GS.addProperty(conceptIRI, graph, labelProp, av, lang);
                                        }
                                    });
                                }
                            });
                        }
                    }
                });
                // Notes as resources
                for (let langProp of LANG_PROPS) {
                    if (mergeList.includes(langProp)) {
                        const noteResources = targetData["http://www.w3.org/2004/02/skos/core#" + langProp];
                        if (noteResources) {
                            const noteResourcesIRIs = Array.isArray(noteResources) ?
                                noteResources.map(nr => nr.id)
                                :
                                [noteResources.id];
                            for (let noteIRI of noteResourcesIRIs) {
                                const noteData = GS.data(noteIRI, graph);
                                const commentLang = Object.keys(noteData.comment)[0];
                                const comment = noteData.comment[commentLang];
                                await this.addNewComment(langProp, commentLang, comment);
                            }
                        }
                    }
                }
                this.exitMergeConcept();
            }
            catch (e) {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                this.setState({ isErrored: e });
            }
        });
    };

    exitMergeConcept = () => this.setState({
        open: false,
        target: undefined,
        mergeList: MERGE_PROPS.map(mp => mp.property)
    });

    handleMergePropertyChange = (event) => {
        const checked = event.target.checked;
        const property = event.target.name;
        const mergeList = [...this.state.mergeList];
        if (checked) {
            if (!mergeList.includes(property)) mergeList.push(property);
        }
        else {
            if (mergeList.includes(property)) mergeList.splice(mergeList.indexOf(property), 1);
        }
        this.setState({ mergeList });
    };

    // We need to bubble up from the resource data in certain updates
    monitorLinkUpdate = (iri) => {
        const data = GS.data(iri, this.props.graph, false);
        let disabled = false;
        if (data.deprecated === true || data.pendingDeletion === true) {
            disabled = true;
        }
        this.setState({
            disabled
        });
    };

    render() {

        const { classes, graph, schemeIRI, useWorkflow, conceptIRI, defaultLang } = this.props;
        const { target, mergeList, isErrored, merging } = this.state;

        if (isErrored) return <Errored dataTest="md-error" data={isErrored} />;

        const disabled = this.state.disabled || !target || mergeList.length === 0;

        return (
            <React.Fragment>
                <span data-test="merge-concept" onClick={this.handleClickOpen}>
                    <Tooltip placement="right" title='Merge concept'>
                        <IconButton
                            aria-label="Merge concept"
                            size="small"
                            className={classes.addButton}>
                            <MergeTypeIcon />
                        </IconButton>
                    </Tooltip>
                </span>
                {this.state.open &&
                    <Dialog
                        data-test="merge-concept-dialog"
                        fullWidth
                        maxWidth="lg"
                        classes={{
                            paper: classes.paper,
                            paperWidthLg: classes.largeDialog
                        }}
                        className={classes.dialog}
                        open={true}
                        onClose={this.exitMergeConcept}
                        aria-labelledby="merge-concept-dialog-title">
                        <DialogTitle disableTypography className={classes.mainHeading} id="merge-concept-dialog-title">Merge Concept</DialogTitle>
                        <DialogContent style={{ height: 400 }}>
                            <div>
                                <TitledPanel
                                    overflow={true}
                                    dataTest="concept-to-merge"
                                    title="Select Concept to Merge">
                                    {target ?
                                        <ConceptChip
                                            id={target}
                                            graph={graph}
                                            disabled={merging}
                                            updateEvent={this.monitorLinkUpdate}
                                            deleteEvent={this.monitorLinkUpdate}
                                            onRemove={this.removeTarget}
                                            loadOptions={{
                                                language: defaultLang
                                            }}
                                            defaultLang={defaultLang} />
                                        :
                                        <div style={{ height: 43, overflow: 'visible' }}>
                                            <FindConcept
                                                source={conceptIRI}
                                                schemeIRI={schemeIRI}
                                                graph={graph}
                                                useWorkflow={useWorkflow}
                                                exclude={[conceptIRI]}
                                                onSelectConcept={this.setTarget} />
                                        </div>
                                    }
                                </TitledPanel>
                                <TitledPanel
                                    margin={true}
                                    title="What to Merge">
                                    <div style={{ marginLeft: 12 }}>
                                        <Grid container>
                                            {
                                                MERGE_PROPS.map(propItem =>
                                                    <Grid item xs={4} key={propItem.property}>
                                                        <FormControlLabel
                                                            data-test={"merge-" + propItem.property}
                                                            control={
                                                                <Checkbox key={propItem.property}
                                                                    size="small"
                                                                    color="primary"
                                                                    data-test={"checkbox-" + propItem.property}
                                                                    disabled={!target || merging}
                                                                    name={propItem.property}
                                                                    className={classes.checked}
                                                                    checked={mergeList.includes(propItem.property)}
                                                                    onClick={(ev) => this.handleMergePropertyChange(ev)} />}
                                                            label={propItem.label}
                                                            classes={{ label: classes.formLabelInline }}
                                                        />
                                                    </Grid>
                                                )
                                            }
                                        </Grid>
                                    </div>
                                </TitledPanel>
                            </div>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <CancelButton onClick={this.exitMergeConcept} disabled={merging} />
                            <PrimaryButton onClick={this.mergeConcept} disabled={disabled || merging}>Merge</PrimaryButton>
                        </DialogActions>
                    </Dialog>
                }
            </React.Fragment>
        );
    }
}

MergeDialog.defaultProps = {
};

MergeDialog.propTypes = {
    classes: PropTypes.object,
    schemeIRI: PropTypes.string.isRequired,
    conceptIRI: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    errorText: PropTypes.string,
    useSlashIRI: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    useCounterIRI: PropTypes.bool,
    counters: PropTypes.object,
    matching: PropTypes.bool,
    graph: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool.isRequired,
    viewLang: PropTypes.string,
    exclude: PropTypes.array,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(MergeDialog);